import { useBookings } from "PFCore/hooks/queries";
import { Booking, Id, PaginatedCollection } from "PFTypes";
import { useMemo } from "react";

import { PER_PAGE_UNPAGINATED } from "../../constants/unpaginated";

type UseFutureBookingsReturn = {
  bookings: PaginatedCollection<Booking[], object> | undefined;
  futureBookings: Booking[] | undefined;
  isFirstBooking: boolean;
  hasFutureBookings: boolean;
  isFetching: boolean;
};

type UseFutureBookingsParams = {
  thisBooking: { startDate: string; id?: Id };
  activityId?: Id;
  profileId: number;
  bookingTemplateId?: number;
};

export const useFutureBookings = ({
  thisBooking,
  activityId,
  profileId,
  bookingTemplateId
}: UseFutureBookingsParams): UseFutureBookingsReturn => {
  const { data: bookings, isFetching } = useBookings(
    {
      profileId,
      activityId: activityId as number,
      perPage: PER_PAGE_UNPAGINATED,
      bookingTemplateId
    },
    { enabled: !!thisBooking.id }
  );

  const isFirstBooking = useMemo(() => {
    const thisBookingStartDateTimestamp = new Date(thisBooking.startDate).getTime();
    return (
      bookings?.entries
        ?.filter(({ id }) => id !== thisBooking.id)
        .every(
          ({ start_date: startDate }) => new Date(startDate).getTime() >= thisBookingStartDateTimestamp
        ) ?? false
    );
  }, [bookings, thisBooking.id, thisBooking.startDate]);

  const hasFutureBookings = useMemo(() => {
    const thisBookingStartDateTimestamp = new Date(thisBooking.startDate).getTime();
    return (
      bookings?.entries
        ?.filter(({ id }) => id !== thisBooking.id)
        .some(
          ({ start_date: startDate }) => new Date(startDate).getTime() >= thisBookingStartDateTimestamp
        ) ?? false
    );
  }, [bookings, thisBooking.id, thisBooking.startDate]);

  const futureBookings = useMemo(() => {
    const thisBookingStartDateTimestamp = new Date(thisBooking.startDate).getTime();
    return bookings?.entries?.filter(
      ({ start_date }) => new Date(start_date).getTime() >= thisBookingStartDateTimestamp
    );
  }, [bookings, thisBooking.id, thisBooking.startDate]);

  return { bookings, futureBookings, isFirstBooking, hasFutureBookings, isFetching };
};
